import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";

import { cn } from "@/lib/utils";

const VERSIONS = [
  {
    name: "Original",
    label: "Original mix",
    src: "/marketing/mastering/original.mp3",
  },
  {
    name: "Mastered",
    label: "Lutely master",
    src: "/marketing/mastering/mastered.mp3",
  },
];

const SEEK_STEPS: Record<string, number> = {
  ArrowLeft: -5,
  ArrowDown: -5,
  ArrowRight: 5,
  ArrowUp: 5,
  PageDown: -15,
  PageUp: 15,
};

const SLIDER_THUMB =
  "[&::-webkit-slider-thumb]:size-4 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:bg-teal-500 [&::-webkit-slider-thumb]:shadow-[0_2px_8px_rgba(0,0,0,0.35)] [&::-moz-range-thumb]:size-[11px] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:bg-teal-500 [&::-moz-range-thumb]:shadow-[0_2px_8px_rgba(0,0,0,0.35)]";

function formatTime(seconds: number) {
  const safe = Number.isFinite(seconds) ? Math.max(0, seconds) : 0;
  return `${Math.floor(safe / 60)}:${String(Math.floor(safe % 60)).padStart(2, "0")}`;
}

/** Hero player that switches between the original mix and the Lutely master. */
export default function MasteringComparison() {
  const titleId = useId();
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingRef = useRef<{ position: number; resume: boolean } | null>(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    return () => audio?.pause();
  }, []);

  const mastered = active === 1;
  const progress = duration ? (position / duration) * 100 : 0;

  const seek = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = Math.min(Math.max(0, time), duration);
    if (pendingRef.current) pendingRef.current.position = next;
    else audio.currentTime = next;
    setPosition(next);
  };

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  };

  const selectVersion = (index: number) => {
    const audio = audioRef.current;
    if (!audio || index === active) return;
    pendingRef.current ??= {
      position: audio.currentTime,
      resume: !audio.paused,
    };
    audio.pause();
    setActive(index);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    setDuration(audio.duration);
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;
    audio.currentTime = Math.min(
      pending.position,
      Math.max(0, audio.duration - 0.1),
    );
    setPosition(audio.currentTime);
    if (pending.resume) audio.play().catch(() => {});
  };

  const handleSeekKey = (event: KeyboardEvent<HTMLInputElement>) => {
    const step = SEEK_STEPS[event.key];
    if (step === undefined) return;
    event.preventDefault();
    seek(position + step);
  };

  return (
    <section
      aria-labelledby={titleId}
      className="relative z-[1] w-[min(600px,100%)] rotate-1 rounded-[26px] border border-white/[0.72] bg-white p-7 font-open-sans text-marketing-ink shadow-[0_30px_90px_rgba(0,0,0,0.38)] max-[680px]:rotate-0 max-[680px]:px-5 max-[680px]:py-[25px]"
    >
      <h2
        id={titleId}
        className="mb-5 whitespace-nowrap font-figtree text-[clamp(28px,3vw,42px)] font-black uppercase leading-none tracking-[-0.045em] max-[680px]:whitespace-normal"
      >
        Hear the difference.
      </h2>
      <div className="overflow-hidden rounded-[18px] border border-[#DCE2F2] bg-white">
        <audio
          ref={audioRef}
          preload="metadata"
          src={VERSIONS[active].src}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={(e) => {
            if (!pendingRef.current) setPosition(e.currentTarget.currentTime);
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={(e) => {
            e.currentTarget.currentTime = 0;
            setPosition(0);
          }}
        />
        <div
          className={cn(
            "px-[22px] pb-5 pt-6 transition-[background,box-shadow] duration-200",
            mastered &&
              "bg-[linear-gradient(90deg,rgba(66,221,168,0.1),rgba(255,255,255,0)_82%)] shadow-[inset_3px_0_#43DDA7]",
          )}
        >
          <div className="grid grid-cols-[42px_minmax(0,1fr)_36px_36px] items-center gap-[10px] max-[680px]:grid-cols-[40px_minmax(0,1fr)_34px] max-[680px]:gap-2">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={`${playing ? "Pause" : "Play"} ${VERSIONS[active].label}`}
              className={cn(
                "size-[42px] cursor-pointer rounded-full border-0 text-[15px] max-[680px]:size-10",
                mastered
                  ? "bg-teal-500 text-marketing-navy"
                  : playing
                    ? "bg-marketing-blue text-white"
                    : "bg-[#6F7687] text-white",
              )}
            >
              <span aria-hidden>{playing ? "Ⅱ" : "▶︎"}</span>
            </button>
            <div className="relative flex h-12 items-center max-[680px]:h-[46px]">
              <div
                aria-hidden
                className={cn(
                  "pointer-events-none absolute inset-0 opacity-[0.62] [mask-image:linear-gradient(180deg,transparent_5%,#000_28%,#000_72%,transparent_95%)]",
                  mastered
                    ? "[background:repeating-linear-gradient(90deg,#28ae87_0_2px,transparent_2px_7px)]"
                    : "[background:repeating-linear-gradient(90deg,#9097a8_0_2px,transparent_2px_7px)]",
                )}
              />
              <input
                type="range"
                min={0}
                max={duration}
                step={0.01}
                value={position}
                onChange={(e) => seek(Number(e.target.value))}
                onKeyDown={handleSeekKey}
                aria-label="Audio position"
                aria-valuetext={`${formatTime(position)} of ${formatTime(duration)}`}
                style={{ "--progress": `${progress}%` } as CSSProperties}
                className={cn(
                  "relative z-[1] m-0 h-[3px] w-full cursor-pointer appearance-none rounded-full outline-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[6px] focus-visible:outline-blue-600",
                  mastered
                    ? "[background:linear-gradient(90deg,#43DDA7_0_var(--progress,0%),rgba(92,100,122,0.22)_var(--progress,0%)_100%)]"
                    : "[background:linear-gradient(90deg,#3042E2_0_var(--progress,0%),rgba(92,100,122,0.22)_var(--progress,0%)_100%)]",
                  SLIDER_THUMB,
                )}
              />
            </div>
            <span className="text-[12px] font-bold text-[#7A8294]">
              {formatTime(position)}
            </span>
            <span className="text-[12px] font-bold text-[#7A8294] max-[680px]:hidden">
              {formatTime(duration)}
            </span>
          </div>
          <div
            role="group"
            aria-label="Choose audio version"
            className="mt-5 grid grid-cols-2 gap-[10px]"
          >
            {VERSIONS.map((version, i) => {
              const selected = i === active;
              return (
                <button
                  key={version.name}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => selectVersion(i)}
                  className={cn(
                    "min-h-[44px] cursor-pointer rounded-[13px] border px-4 py-[10px] font-figtree text-[14px] font-extrabold leading-[1.2] transition-[transform,border-color,color,background] duration-[180ms] motion-safe:hover:-translate-y-px",
                    !selected
                      ? "border-[#DFE3ED] bg-white text-[#596176] hover:border-[#AEB7CB]"
                      : i === 0
                        ? "border-blue-600 bg-[#EEF1FF] text-blue-600"
                        : "border-teal-500 bg-[#EAFFF7] text-[#0F7F65]",
                  )}
                >
                  {version.name}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
